// E8SumFormulaClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 8) – uzupelnij zdania A/B oraz C/D.
// S = 1+2+...+n = 1/2 n(n+1). Suma do m oraz poprawne przeksztalcenie wzoru.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

function ChoiceBox({ labels }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        border: "1.5px solid currentColor",
        borderRadius: 4,
        padding: "1px 8px",
        margin: "0 4px",
        opacity: 0.75,
        verticalAlign: "middle",
        fontSize: "0.9em",
      }}
    >
      {labels.map((label, index) => (
        <React.Fragment key={label}>
          {index > 0 && <span style={{ opacity: 0.5 }}>|</span>}
          <strong>
            <InlineMath math={label} />
          </strong>
        </React.Fragment>
      ))}
    </span>
  );
}

const M_VALUES = [20, 40, 50, 100, 200];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const FORM_CORRECT = "S=\\frac{1}{2}n^2+\\frac{1}{2}n";
const FORM_WRONG = "S=\\frac{1}{2}n^2+1";

function buildData({ m, shuffleOptions = true }) {
  const sumCorrect = (m * (m + 1)) / 2;
  const sumWrong = (m * m) / 2 + 1; // blad: uzycie 1/2 n^2 + 1

  let g1 = [
    { value: sumWrong, latex: `${sumWrong}`, correct: false },
    { value: sumCorrect, latex: `${sumCorrect}`, correct: true },
  ];
  let g2 = [
    { value: FORM_WRONG, latex: FORM_WRONG, correct: false },
    { value: FORM_CORRECT, latex: FORM_CORRECT, correct: true },
  ];
  if (shuffleOptions) {
    g1 = shuffle(g1);
    g2 = shuffle(g2);
  }

  const G1_LETTERS = ["A", "B"];
  const G2_LETTERS = ["C", "D"];
  return {
    m,
    sumCorrect,
    g1: g1.map((o, i) => ({ ...o, letter: G1_LETTERS[i] })),
    g2: g2.map((o, i) => ({ ...o, letter: G2_LETTERS[i] })),
    correct: {
      s1: G1_LETTERS[g1.findIndex((o) => o.correct)],
      s2: G2_LETTERS[g2.findIndex((o) => o.correct)],
    },
  };
}

function createOriginalTask() {
  return {
    type: "E8SumFormulaClosedTask",
    data: buildData({ m: 100, shuffleOptions: false }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 8,
    },
    tags: ["e8", "algebra", "wzory", "przeksztalcanie"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8SumFormulaClosedTask",
    data: buildData({ m: pick(M_VALUES) }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_8" },
    tags: ["e8", "algebra", "wzory", "przeksztalcanie"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Sumę <InlineMath math="S=1+2+3+\ldots+n" /> kolejnych liczb naturalnych od{" "}
        <InlineMath math="1" /> do <InlineMath math="n" /> można obliczyć ze wzoru
      </p>
      <BlockMath math="S=\frac{1}{2}n(n+1)" />
      <p>
        <strong>
          Uzupełnij zdania. Wybierz odpowiedź spośród oznaczonych literami{" "}
          <InlineMath math="A" /> i <InlineMath math="B" /> oraz odpowiedź
          spośród oznaczonych literami <InlineMath math="C" /> i{" "}
          <InlineMath math="D" />.
        </strong>
      </p>
      <p style={{ marginBottom: 4 }}>
        Suma <InlineMath math={`${data.m}`} /> kolejnych liczb naturalnych od{" "}
        <InlineMath math="1" /> do <InlineMath math={`${data.m}`} /> jest równa{" "}
        <ChoiceBox labels={["A", "B"]} />.
      </p>
      <p style={{ marginTop: 0, marginLeft: 16 }}>
        {data.g1.map((o, index) => (
          <React.Fragment key={o.letter}>
            {index > 0 && <span style={{ marginLeft: 36 }} />}
            <ChoiceOptionMath letter={o.letter} math={o.latex} />
          </React.Fragment>
        ))}
      </p>
      <p style={{ marginBottom: 4 }}>
        Wzór na sumę <InlineMath math="S" /> po poprawnym przekształceniu ma postać{" "}
        <ChoiceBox labels={["C", "D"]} />.
      </p>
      <p style={{ marginTop: 0, marginLeft: 16 }}>
        {data.g2.map((o, index) => (
          <React.Fragment key={o.letter}>
            {index > 0 && <span style={{ marginLeft: 36 }} />}
            <ChoiceOptionMath letter={o.letter} math={o.latex} />
          </React.Fragment>
        ))}
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const answer = userAnswer || {};
  return (
    <div style={{ display: "grid", rowGap: 14, marginTop: 10 }}>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math={data.g1[0].letter} /> lub{" "}
          <InlineMath math={data.g1[1].letter} />:
        </div>
        <ABCDInput
          options={data.g1.map((o) => ({
            label: o.letter,
            textNode: <InlineMath math={o.letter} />,
          }))}
          value={answer.s1 ?? null}
          onChange={(s1) => onChange({ ...answer, s1 })}
          disabled={disabled}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math={data.g2[0].letter} /> lub{" "}
          <InlineMath math={data.g2[1].letter} />:
        </div>
        <ABCDInput
          options={data.g2.map((o) => ({
            label: o.letter,
            textNode: <InlineMath math={o.letter} />,
          }))}
          value={answer.s2 ?? null}
          onChange={(s2) => onChange({ ...answer, s2 })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    data.g1[userAnswer?.s1]?.letter === data.correct.s1 &&
    data.g2[userAnswer?.s2]?.letter === data.correct.s2
  );
}

export function renderAnswer({ data }) {
  return (
    <InlineMath math={`\\text{${data.correct.s1}},\\ \\text{${data.correct.s2}}`} />
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  const l1 = data.g1[userAnswer?.s1]?.letter;
  const l2 = data.g2[userAnswer?.s2]?.letter;
  if (!l1 && !l2) return <i>(brak odpowiedzi)</i>;
  return (
    <InlineMath math={`\\text{${l1 || "-"}},\\ \\text{${l2 || "-"}}`} />
  );
}

export function renderExplanation({ data }) {
  const { m, sumCorrect } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Podstawiamy <InlineMath math={`n=${m}`} /> do wzoru:
      </p>
      <BlockMath
        math={`S=\\frac{1}{2}\\cdot ${m}\\cdot(${m}+1)=\\frac{1}{2}\\cdot ${m}\\cdot ${m + 1}=${sumCorrect}`}
      />
      <p>
        <strong>Krok 2.</strong> Przekształcamy wzór, mnożąc{" "}
        <InlineMath math="\frac{1}{2}n" /> przez sumę w nawiasie:
      </p>
      <BlockMath math="S=\frac{1}{2}n(n+1)=\frac{1}{2}n^2+\frac{1}{2}n" />
      <p>
        Poprawne odpowiedzi:{" "}
        <InlineMath
          math={`\\text{${data.correct.s1}}\\ \\text{i}\\ \\text{${data.correct.s2}}`}
        />
        .
      </p>
    </div>
  );
}
